import { formatScore, plural, toAccusative } from '@/shared/lib';

import { DrawPoolType, PrizeKind } from '../model/types';
import type { CurrentDraws, Prize, SeasonFinalDraw } from '../model/types';

/**
 * Строки блока «Призы месяца».
 *
 * Разметку строит каждый виджет своими классами (у промо и у экрана
 * рейтинга разные модули стилей), а вот что именно писать — общее
 * знание, и жить оно должно в одном месте.
 */
export interface PrizeLine {
  /** Ключ для React и для тестов. */
  poolType: DrawPoolType;
  /** «топ-10» — выделяется в вёрстке отдельно. */
  poolLabel: string;
  /** Сколько победителей в пуле. */
  winnersCount: number;
  /** Вид приза; `null` — приз за пулом не закреплён. */
  prizeKind: PrizeKind | null;
  /** Название приза; `null` — приз за пулом не закреплён. */
  prizeTitle: string | null;
  /** Сколько Фрибетов входит в приз. Ноль — Фрибетов нет. */
  freebetAmount: number;
}

const POOL_LABEL: Record<DrawPoolType, string> = {
  [DrawPoolType.TOP_10]: 'топ-10',
  [DrawPoolType.TOP_100]: 'топ-100',
  [DrawPoolType.TOP_1000]: 'топ-1000',
};

/** Общая форма пула: подходит и анонсу, и проведённому розыгрышу. */
interface PoolLike {
  type: DrawPoolType;
  winnersCount: number;
  prize: Prize | null;
}

/**
 * `null` — показывать статический текст макета.
 *
 * Так выглядит цикл, в котором оператор ещё не назначил ни одного приза:
 * границы пулов известны всегда (они в шаблоне настроек), а призов нет.
 * Подменять их выдумкой нельзя.
 */
export function prizeLinesOf(pools: PoolLike[]): PrizeLine[] | null {
  if (pools.length === 0) return null;
  if (pools.every((pool) => pool.prize === null)) return null;

  return pools.map((pool) => ({
    poolType: pool.type,
    poolLabel: POOL_LABEL[pool.type],
    winnersCount: pool.winnersCount,
    prizeKind: pool.prize?.kind ?? null,
    prizeTitle: pool.prize?.title ?? null,
    freebetAmount: pool.prize?.freebetAmount ?? 0,
  }));
}

/** Человекочитаемая награда пула: «Яндекс Станция и 5000 фрибетов». */
export function prizeLineText(line: PrizeLine): string {
  const freebets =
    line.freebetAmount > 0
      ? `${formatScore(line.freebetAmount)} фрибетов`
      : null;

  // У чисто фрибетового приза название дублирует номинал: в макете
  // строка звучит «получат по 2000 фрибетов», а не «по Фрибеты и 2000
  // фрибетов». Название такого приза нужно оператору в админке, а игроку
  // важен только номинал
  if (line.prizeKind === PrizeKind.FREEBETS && freebets) return freebets;

  if (line.prizeTitle && freebets) return `${line.prizeTitle} и ${freebets}`;
  return line.prizeTitle ?? freebets ?? 'приз';
}

/**
 * Как называют победителей пула — по макету.
 *
 * У топ-1000 в макете «предсказатели», у топ-100 «счастливчики»: это
 * копирайт, а не логика, поэтому таблицей. Форм три, потому что число
 * победителей приходит из настройки и оператор волен поставить хоть две.
 */
const POOL_WINNER_NOUN: Record<DrawPoolType, [string, string, string]> = {
  [DrawPoolType.TOP_10]: ['счастливчик', 'счастливчика', 'счастливчиков'],
  [DrawPoolType.TOP_100]: ['счастливчик', 'счастливчика', 'счастливчиков'],
  [DrawPoolType.TOP_1000]: ['предсказатель', 'предсказателя', 'предсказателей'],
};

/**
 * Фраза о призе пула, разобранная на части.
 *
 * Целой строкой не отдаём: в вёрстке название пула и сам приз выделены,
 * а склеивать разметку в `entities` нельзя — у промо и у экрана рейтинга
 * разные модули стилей.
 */
export interface PrizePhrase {
  /** До названия пула: «Среди » или «15 счастливчиков из ». */
  before: string;
  /** «топ-10» — выделяется. */
  pool: string;
  /** Между пулом и призом: « разыграем » или « получат по ». */
  between: string;
  /** «Яндекс Станция и 5000 фрибетов» — выделяется. */
  prize: string;
}

/**
 * Фраза по макету: «Среди топ-10 разыграем Яндекс Станцию и 5000 фрибетов»,
 * «15 счастливчиков из топ-100 получат по 2000 фрибетов».
 *
 * Единственный победитель получает безличную форму — так в макете у топ-10,
 * и так же читается любой пул, где приз один. Больше одного — счёт нужен,
 * иначе игрок не поймёт свои шансы.
 *
 * «по» ставится только при известном призе: «получат по приз» — не текст.
 */
export function prizePhraseOf(line: PrizeLine): PrizePhrase {
  // Название лежит в базе в именительном, а обе фразы требуют винительного
  const prize = toAccusative(prizeLineText(line));

  if (line.winnersCount === 1) {
    return {
      before: 'Среди ',
      pool: line.poolLabel,
      between: ' разыграем ',
      prize,
    };
  }

  // «по» уместно только при голом количестве Фрибетов («получат по 2000
  // фрибетов»). С названием получилось бы «получат по Яндекс Станцию» —
  // предлог требует дательного, и склонять пришлось бы иначе. Проще
  // и честнее сказать «получат Яндекс Станцию»
  const isAmountOnly =
    line.prizeTitle === null || line.prizeKind === PrizeKind.FREEBETS;
  const hasPrize = line.prizeTitle !== null || line.freebetAmount > 0;

  const [one, few, many] = POOL_WINNER_NOUN[line.poolType];
  return {
    before: `${formatScore(line.winnersCount)} ${plural(line.winnersCount, one, few, many)} из `,
    pool: line.poolLabel,
    between: hasPrize && isAmountOnly ? ' получат по ' : ' получат ',
    prize,
  };
}

/**
 * Что показывать, пока у цикла нет плана призов.
 *
 * Раньше эти три строки лежали разметкой в двух виджетах сразу — на главной
 * и на экране «Розыгрыш», — и разъехались с базой: там с 28.08.2026 главный
 * приз PS5 SLIM, а в вёрстке осталась «Яндекс Станция» из макета. Теперь
 * данные одни, и рисуются они тем же `prizePhraseOf`, что и настоящий анонс:
 * разойтись больше нечему.
 *
 * Значения — сид `08-prizes.ts` и настройки пулов: 1 × PS5 SLIM + 5000,
 * 15 × 2000, 50 × 600.
 *
 * ЭТО ЗАПАСНОЙ ВАРИАНТ, А НЕ ИСТОЧНИК ПРАВДЫ. Заполнен план у цикла —
 * показывается он. Пустой план сейчас у циклов с третьего (лист «Призы»
 * задаёт только сентябрь и октябрь), то есть текст ниже всплывёт в ноябре
 * и будет обещать приз ПЕРВОГО месяца. Открытый вопрос 5.
 */
export const FALLBACK_PRIZE_LINES: PrizeLine[] = [
  {
    poolType: DrawPoolType.TOP_10,
    poolLabel: POOL_LABEL[DrawPoolType.TOP_10],
    winnersCount: 1,
    prizeKind: PrizeKind.PHYSICAL_WITH_FREEBETS,
    prizeTitle: 'PS5 SLIM',
    freebetAmount: 5000,
  },
  {
    poolType: DrawPoolType.TOP_100,
    poolLabel: POOL_LABEL[DrawPoolType.TOP_100],
    winnersCount: 15,
    prizeKind: PrizeKind.FREEBETS,
    prizeTitle: '2 000 фрибетов',
    freebetAmount: 2000,
  },
  {
    poolType: DrawPoolType.TOP_1000,
    poolLabel: POOL_LABEL[DrawPoolType.TOP_1000],
    winnersCount: 50,
    prizeKind: PrizeKind.FREEBETS,
    prizeTitle: '600 фрибетов',
    freebetAmount: 600,
  },
];

/**
 * Запасной текст «Финального приза».
 *
 * Дата «10 июня» пришла из макета и ничем не подтверждена: ГДД §3.4
 * называет только месяц (открытый вопрос 50). Лежала так же в двух местах.
 */
export const FALLBACK_SEASON_TEXT =
  'Итоги подведём 10 июня, а пока продолжай активничать в канале и делать прогнозы, фрибеты сами себя не получат';

/** Строки «Призов месяца» из анонса. `null` — призы ещё не назначены. */
export function currentPrizeLines(
  current: CurrentDraws | null,
): PrizeLine[] | null {
  return current === null ? null : prizeLinesOf(current.pools);
}

/**
 * Текст блока «Финальный приз». `null` — приз не назначен, показывается
 * текст макета.
 */
export function seasonFinalText(
  source:
    | Pick<SeasonFinalDraw, 'prize' | 'tripCity' | 'winningClub'>
    | null
    | undefined,
): string | null {
  if (!source) return null;

  const parts: string[] = [];
  if (source.prize?.title) parts.push(source.prize.title);
  if (source.tripCity) parts.push(`Поездка: ${source.tripCity}`);
  if (source.winningClub) parts.push(`Клуб: ${source.winningClub}`);

  return parts.length > 0 ? parts.join('. ') : null;
}

/** То же для анонса: приз сезона лежит там плоскими полями. */
export function currentSeasonText(current: CurrentDraws | null): string | null {
  if (current === null) return null;
  return seasonFinalText({
    prize: current.seasonPrize,
    tripCity: current.seasonTripCity,
    winningClub: current.seasonWinningClub,
  });
}
